import React, { useState, useRef, useEffect } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

const TopNavigation = ({ allPanelsOpen, toggleAllPanels, onCut, onCopy, onPaste, onDelete, onUndo, onRedo, hasSelection, hasClipboard, canUndo, canRedo }) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('Downloads');
  const [fileName, setFileName] = useState('ai-studio-export');
  const [selectedFormat, setSelectedFormat] = useState('png'); // Default to PNG
  const menuRef = useRef(null);

  // Detect if we're on Mac
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const cmdKey = isMac ? 'Cmd' : 'Ctrl';

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const menuItems = {
    File: [
      { label: 'New', shortcut: `${cmdKey}+N` },
      { label: 'Open...', shortcut: `${cmdKey}+O` },
      { label: 'Save', shortcut: `${cmdKey}+S` },
      { label: 'Save As...', shortcut: `${cmdKey}+Shift+S` },
      { label: 'Export...', shortcut: `${cmdKey}+E` },
      { label: 'Import...', shortcut: `${cmdKey}+I` },
      { label: '---' },
      { label: 'Recent Files' },
      { label: '---' },
      { label: 'Close', shortcut: `${cmdKey}+W` }
    ],
    Edit: [
      { label: 'Undo', shortcut: `${cmdKey}+Z` },
      { label: 'Redo', shortcut: `${cmdKey}+Y` },
      { label: '---' },
      { label: 'Cut', shortcut: `${cmdKey}+X` },
      { label: 'Copy', shortcut: `${cmdKey}+C` },
      { label: 'Paste', shortcut: `${cmdKey}+V` },
      { label: 'Duplicate', shortcut: `${cmdKey}+D` },
      { label: '---' },
      { label: 'Select All', shortcut: `${cmdKey}+A` },
      { label: 'Deselect All', shortcut: `${cmdKey}+Shift+A` },
      { label: '---' },
      { label: 'Preferences...', shortcut: `${cmdKey}+,` }
    ],
    View: [
      { label: 'Zoom In', shortcut: `${cmdKey}++` },
      { label: 'Zoom Out', shortcut: `${cmdKey}+-` },
      { label: 'Zoom to Fit', shortcut: `${cmdKey}+0` },
      { label: 'Actual Size', shortcut: `${cmdKey}+1` },
      { label: '---' },
      { label: 'Show Grid', shortcut: `${cmdKey}+G` },
      { label: 'Show Rulers', shortcut: `${cmdKey}+R` },
      { label: 'Show Guides', shortcut: `${cmdKey}+;` },
      { label: '---' },
      { label: 'Fullscreen', shortcut: 'F11' },
      { label: 'Toggle Panels', shortcut: 'Tab' }
    ],
    Layer: [
      { label: 'New Layer', shortcut: `${cmdKey}+Shift+N` },
      { label: 'Duplicate Layer', shortcut: `${cmdKey}+J` },
      { label: 'Delete Layer', shortcut: 'Delete' },
      { label: '---' },
      { label: 'Group Layers', shortcut: `${cmdKey}+G` },
      { label: 'Ungroup Layers', shortcut: `${cmdKey}+Shift+G` },
      { label: '---' },
      { label: 'Move to Front', shortcut: `${cmdKey}+]` },
      { label: 'Move Forward', shortcut: `${cmdKey}+]` },
      { label: 'Move Backward', shortcut: `${cmdKey}+[` },
      { label: 'Move to Back', shortcut: `${cmdKey}+Shift+[` },
      { label: '---' },
      { label: 'Lock Layer', shortcut: `${cmdKey}+L` },
      { label: 'Hide Layer', shortcut: `${cmdKey}+H` }
    ],
    Help: [
      { label: 'User Guide' },
      { label: 'Keyboard Shortcuts', shortcut: `${cmdKey}+/` },
      { label: 'Video Tutorials' },
      { label: '---' },
      { label: 'AI Assistant', shortcut: `${cmdKey}+Space` },
      { label: 'Community Forum' },
      { label: '---' },
      { label: 'Report Bug' },
      { label: 'Feature Request' },
      { label: '---' },
      { label: 'About Mark Sexton AI Studio' }
    ]
  };

  const handleMenuClick = (menuName) => {
    setActiveMenu(activeMenu === menuName ? null : menuName);
  };

  // Export dialog with format selection
  const showExportDialog = () => {
    try {
      console.log('Starting export dialog...');
      
      // Find the canvas SVG element specifically
      const svgElement = document.querySelector('.canvas-area svg');
      if (!svgElement) {
        alert('No canvas content found to export.');
        return;
      }
      
      // Show the custom modal instead of browser prompt
      setShowExportModal(true);
      
    } catch (error) {
      console.error('Export dialog error:', error);
      alert('Export failed: ' + error.message);
    }
  };

  // Handle export format selection (toggle between formats)
  const handleFormatToggle = (format) => {
    setSelectedFormat(format);
    console.log(`Selected format: ${format}`);
  };

  // Handle actual export action
  const handleExport = () => {
    setShowExportModal(false);
    
    const svgElement = document.querySelector('.canvas-area svg');
    if (!svgElement) {
      alert('No canvas content found to export.');
      return;
    }
    
    // Use the custom filename from the input
    const customFileName = fileName || 'ai-studio-export';
    
    if (selectedFormat === 'png') {
      exportToPNG(svgElement, customFileName);
    } else if (selectedFormat === 'jpg') {
      exportToJPG(svgElement, customFileName);
    } else if (selectedFormat === 'svg') {
      exportToSVG(svgElement, customFileName);
    }
  };

  // Handle location selection
  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    console.log(`Selected location: ${location}`);
  };

  // Handle filename input change
  const handleFileNameChange = (e) => {
    setFileName(e.target.value);
  };

  // Close export modal
  const closeExportModal = () => {
    setShowExportModal(false);
  };

  // JPG export function
  const exportToJPG = (svgElement, customFileName = 'ai-studio-export') => {
    try {
      console.log('Starting JPG export with filename:', customFileName);
      
      // Temporarily hide all selection handles during export
      const selectionHandles = svgElement.querySelectorAll('g');
      const hiddenElements = [];
      
      selectionHandles.forEach(group => {
        // Hide groups that contain selection handles (resize handles)
        const handles = group.querySelectorAll('rect[fill="white"][stroke="#0066ff"]');
        if (handles.length > 0) {
          group.style.display = 'none';
          hiddenElements.push(group);
        }
      });
      
      // Create a canvas element
      const canvas = document.createElement('canvas');
      canvas.width = 800;
      canvas.height = 600;
      const ctx = canvas.getContext('2d');
      
      // Fill with white background (JPG doesn't support transparency)
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 800, 600);
      
      // Convert SVG to data URL
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
      const svgUrl = URL.createObjectURL(svgBlob);
      
      // Create an image and draw it to canvas
      const img = new Image();
      img.onload = function() {
        ctx.drawImage(img, 0, 0, 800, 600);
        
        // Restore hidden elements
        hiddenElements.forEach(element => {
          element.style.display = '';
        });
        
        // Convert canvas to JPG and download
        canvas.toBlob(function(blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `${customFileName}.jpg`;
          link.href = url;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          console.log(`JPG export completed: ${customFileName}.jpg`);
        }, 'image/jpeg', 0.95); // 95% quality for JPG
        
        URL.revokeObjectURL(svgUrl);
      };
      
      img.onerror = function() {
        // Restore hidden elements on error
        hiddenElements.forEach(element => {
          element.style.display = '';
        });
        console.error('Failed to load SVG for JPG conversion');
        alert('JPG export failed. Please try again.');
        URL.revokeObjectURL(svgUrl);
      };
      
      img.src = svgUrl;
      
    } catch (error) {
      console.error('JPG export error:', error);
      alert('JPG export failed: ' + error.message);
    }
  };

  // PNG export function
  const exportToPNG = (svgElement, customFileName = 'ai-studio-export') => {
    try {
      console.log('Starting PNG export with filename:', customFileName);
      
      // Temporarily hide all selection handles during export
      const selectionHandles = svgElement.querySelectorAll('g');
      const hiddenElements = [];
      
      selectionHandles.forEach(group => {
        // Hide groups that contain selection handles (resize handles)
        const handles = group.querySelectorAll('rect[fill="white"][stroke="#0066ff"]');
        if (handles.length > 0) {
          group.style.display = 'none';
          hiddenElements.push(group);
        }
      });
      
      // Create a canvas element
      const canvas = document.createElement('canvas');
      canvas.width = 800;
      canvas.height = 600;
      const ctx = canvas.getContext('2d');
      
      // Fill with white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 800, 600);
      
      // Convert SVG to data URL
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
      const svgUrl = URL.createObjectURL(svgBlob);
      
      // Create an image and draw it to canvas
      const img = new Image();
      img.onload = function() {
        ctx.drawImage(img, 0, 0, 800, 600);
        
        // Restore hidden elements
        hiddenElements.forEach(element => {
          element.style.display = '';
        });
        
        // Convert canvas to PNG and download
        canvas.toBlob(function(blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `${customFileName}.png`;
          link.href = url;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          console.log(`PNG export completed: ${customFileName}.png`);
        }, 'image/png');
        
        URL.revokeObjectURL(svgUrl);
      };
      
      img.onerror = function() {
        // Restore hidden elements on error
        hiddenElements.forEach(element => {
          element.style.display = '';
        });
        console.error('Failed to load SVG for PNG conversion');
        alert('PNG export failed. Please try again.');
        URL.revokeObjectURL(svgUrl);
      };
      
      img.src = svgUrl;
      
    } catch (error) {
      console.error('PNG export error:', error);
      alert('PNG export failed: ' + error.message);
    }
  };

  // SVG export function
  const exportToSVG = (svgElement, customFileName = 'ai-studio-export') => {
    try {
      console.log('Starting SVG export with filename:', customFileName);
      
      // Temporarily hide all selection handles during export
      const selectionHandles = svgElement.querySelectorAll('g');
      const hiddenElements = [];
      
      selectionHandles.forEach(group => {
        // Hide groups that contain selection handles (resize handles)
        const handles = group.querySelectorAll('rect[fill="white"][stroke="#0066ff"]');
        if (handles.length > 0) {
          group.style.display = 'none';
          hiddenElements.push(group);
        }
      });
      
      // Get SVG content (without handles)
      const svgData = new XMLSerializer().serializeToString(svgElement);
      
      // Restore hidden elements immediately
      hiddenElements.forEach(element => {
        element.style.display = '';
      });
      
      // Create a clean SVG with proper dimensions and background
      const cleanSVG = `<?xml version="1.0" encoding="UTF-8"?>
<svg width="800" height="600" viewBox="-400 -300 800 600" xmlns="http://www.w3.org/2000/svg">
  <!-- White background -->
  <rect x="-400" y="-300" width="800" height="600" fill="white"/>
  ${svgData.replace(/<svg[^>]*>/, '').replace('</svg>', '')}
</svg>`;
      
      // Create blob and download
      const blob = new Blob([cleanSVG], {type: 'image/svg+xml;charset=utf-8'});
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `${customFileName}.svg`;
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log(`SVG export completed: ${customFileName}.svg`);
      
    } catch (error) {
      console.error('SVG export error:', error);
      alert('SVG export failed: ' + error.message);
    }
  };

  const handleMenuItemClick = (item) => {
    console.log(`Menu action: ${item.label}`);
    setActiveMenu(null);
    
    // Handle specific menu actions
    switch (item.label) {
      case 'Export...':
        showExportDialog();
        break;
      case 'Undo':
        if (onUndo && canUndo) onUndo();
        break;
      case 'Redo':
        if (onRedo && canRedo) onRedo();
        break;
      case 'Cut':
        if (onCut && hasSelection) onCut();
        break;
      case 'Copy':
        if (onCopy && hasSelection) onCopy();
        break;
      case 'Paste':
        if (onPaste && hasClipboard) onPaste();
        break;
      case 'Delete Layer':
        if (onDelete && hasSelection) onDelete();
        break;
      default:
        // TODO: Implement other menu actions
        break;
    }
  };

  const renderDropdown = (menuName) => {
    if (activeMenu !== menuName) return null;

    return (
      <div className="absolute top-full left-0 mt-1 border border-gray-600 rounded shadow-lg min-w-[200px] z-50" style={{ backgroundColor: '#424241' }}>
        {menuItems[menuName].map((item, index) => {
          if (item.label === '---') {
            return (
              <div key={index} className="h-px bg-gray-600 my-1" />
            );
          }

          // Check if item should be disabled
          const isDisabled = (
            (item.label === 'Undo') && !canUndo
          ) || (
            (item.label === 'Redo') && !canRedo
          ) || (
            (item.label === 'Cut' || item.label === 'Copy') && !hasSelection
          ) || (
            item.label === 'Paste' && !hasClipboard
          );

          return (
            <button
              key={index}
              onClick={() => !isDisabled && handleMenuItemClick(item)}
              disabled={isDisabled}
              className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors dropdown-menu-item ${
                isDisabled 
                  ? 'text-gray-500 cursor-not-allowed' 
                  : 'text-gray-300 cursor-pointer'
              }`}
              onMouseEnter={(e) => {
                if (!isDisabled) {
                  e.currentTarget.style.backgroundColor = '#000000';
                  e.currentTarget.style.color = 'white';
                  // Also update child spans
                  const spans = e.currentTarget.querySelectorAll('span');
                  spans.forEach(span => {
                    if (span.classList.contains('text-gray-500')) {
                      span.style.color = '#9ca3af';
                    } else {
                      span.style.color = 'white';
                    }
                  });
                }
              }}
              onMouseLeave={(e) => {
                if (!isDisabled) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#d1d5db';
                  // Reset child spans
                  const spans = e.currentTarget.querySelectorAll('span');
                  spans.forEach(span => {
                    if (span.classList.contains('text-gray-500')) {
                      span.style.color = '#6b7280';
                    } else {
                      span.style.color = '#d1d5db';
                    }
                  });
                }
              }}
            >
              <span>{item.label}</span>
              {item.shortcut && (
                <span className="text-xs text-gray-500 ml-4">{item.shortcut}</span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div 
      className="w-full flex items-center px-4 relative"
      style={{ 
        backgroundColor: '#323232',
        height: '44px'
      }}
      ref={menuRef}
    >
      <div className="flex items-center gap-4">
        <div className="text-white font-bold text-2xl pr-11" style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', transform: 'translateY(-3px) translateX(4px)' }}>m</div>
        <span className="text-white text-sm" style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontWeight: '400', letterSpacing: '0.15em', transform: 'translateX(-20px)' }}>Mark Sexton AI Studio</span>
      </div>

      <div className="flex items-center gap-1" style={{ marginLeft: '30px' }}>
        {Object.keys(menuItems).map((menuName) => (
          <div key={menuName} className="relative">
            <button
              onClick={() => handleMenuClick(menuName)}
              className={`px-3 py-1 rounded text-xs font-bold transition-colors flex items-center gap-1 ${
                activeMenu === menuName 
                  ? 'bg-gray-700 text-white' 
                  : 'text-white hover:text-white hover:bg-gray-700'
              }`}
            >
              <span>{menuName}</span>
              <ChevronDown size={12} className="text-white" />
            </button>
            {renderDropdown(menuName)}
          </div>
        ))}
      </div>

      <div className="absolute right-4 flex items-center">
        <button
          onClick={toggleAllPanels}
          className="p-2 rounded transition-colors hover:bg-gray-700"
          title="Toggle all panels"
        >
          {allPanelsOpen ? (
            <ChevronUp size={16} className="text-white" />
          ) : (
            <ChevronDown size={16} className="text-white" />
          )}
        </button>
      </div>
      
      {/* Custom Export Modal */}
      {showExportModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeExportModal}
        >
          <div 
            className="relative bg-white shadow-xl max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: '#2d2d2d', borderRadius: '8px', overflow: 'hidden', height: '550px' }}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b" style={{ borderColor: '#404040', backgroundColor: '#323232' }}>
              <h3 className="text-lg font-medium text-white" style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif' }}>
                Export Canvas
              </h3>
            </div>
            
            {/* Main Content Area */}
            <div className="flex h-full">
              {/* Left Sidebar */}
              <div className="w-48 border-r" style={{ borderColor: '#404040', backgroundColor: '#2a2a2a' }}>
                <div className="p-3">
                  <div className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Location</div>
                  <div className="space-y-1">
                    <div 
                      className="flex items-center px-2 py-1 text-sm bg-blue-600 text-white rounded cursor-default"
                    >
                      <span className="mr-2">📥</span> Downloads
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Main Content */}
              <div className="flex-1 flex flex-col">
                {/* File Browser Area */}
                <div className="flex-1 p-4">
                  <div className="text-sm text-gray-400 mb-6">Choose export format:</div>
                  
                  {/* Export Format Toggle */}
                  <div className="flex space-x-2 mb-6">
                    <button
                      onClick={() => handleFormatToggle('png')}
                      className={`px-6 py-2 text-sm font-medium rounded transition-colors ${
                        selectedFormat === 'png' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      }`}
                    >
                      PNG
                    </button>
                    
                    <button
                      onClick={() => handleFormatToggle('jpg')}
                      className={`px-6 py-2 text-sm font-medium rounded transition-colors ${
                        selectedFormat === 'jpg' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      }`}
                    >
                      JPG
                    </button>
                    
                    <button
                      onClick={() => handleFormatToggle('svg')}
                      className={`px-6 py-2 text-sm font-medium rounded transition-colors ${
                        selectedFormat === 'svg' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      }`}
                    >
                      SVG
                    </button>
                  </div>
                  
                  {/* Export Button */}
                  <div className="mt-4 flex space-x-3">
                    <button
                      onClick={closeExportModal}
                      className="px-6 py-3 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleExport}
                      className="flex-1 py-3 px-4 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition-colors"
                    >
                      Export as {selectedFormat.toUpperCase()}
                    </button>
                  </div>
                  
                  {/* File Name Input - Right below export button */}
                  <div className="mt-2 border-t pt-4" style={{ borderColor: '#404040' }}>
                    <div className="flex items-center space-x-3">
                      <input 
                        type="text" 
                        value={fileName}
                        onChange={handleFileNameChange}
                        className="flex-1 px-3 py-2 text-sm text-white bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                        placeholder="Enter filename"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopNavigation;